import type { MediaTimelineSegment } from "./timelineTypes";

interface Entry {
  segment: MediaTimelineSegment;
  paintOrder: number;
  /** Maximum end in this implicit balanced subtree, not just this segment's end. */
  maxEnd: number;
}

/** Internal immutable index; replace it when the source array identity changes. */
export function createTimelineSegmentIndex(segments: readonly MediaTimelineSegment[]) {
  let minEnd = Infinity;
  const entries: Entry[] = segments
    .map((segment, paintOrder) => {
      minEnd = Math.min(minEnd, segment.endSeconds);
      return { segment, paintOrder, maxEnd: segment.endSeconds };
    })
    .sort((a, b) => a.segment.startSeconds - b.segment.startSeconds || a.paintOrder - b.paintOrder);
  const build = (low: number, high: number): number => {
    if (low >= high) return -Infinity;
    const middle = (low + high) >>> 1;
    const entry = entries[middle]!;
    entry.maxEnd = Math.max(entry.maxEnd, build(low, middle), build(middle + 1, high));
    return entry.maxEnd;
  };
  build(0, entries.length);

  return {
    query(start: number, end: number): readonly MediaTimelineSegment[] {
      if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return [];
      // Every span intersects: preserve the source directly, including dense long overlaps.
      if (
        entries.length > 0 &&
        start < minEnd &&
        entries[entries.length - 1]!.segment.startSeconds < end
      )
        return segments;
      const matches: Entry[] = [];
      const visit = (low: number, high: number): void => {
        if (low >= high) return;
        const middle = (low + high) >>> 1;
        const entry = entries[middle]!;
        if (entry.maxEnd <= start || entries[low]!.segment.startSeconds >= end) return;
        visit(low, middle);
        if (entry.segment.startSeconds < end && entry.segment.endSeconds > start) {
          matches.push(entry);
        }
        visit(middle + 1, high);
      };
      visit(0, entries.length);
      // Time ordering is only a search detail: overlap and active paint keep authored order.
      return matches.sort((a, b) => a.paintOrder - b.paintOrder).map((entry) => entry.segment);
    },
  };
}
